import { useCallback, useEffect, useState } from 'react';
import { accountingPeriodsApi, type AccountingPeriod, type PeriodConfig } from '../lib/api/accounting-periods';
import { useToast } from '../lib/use-toast';
import { AppSelect } from './AppSelect';

const YEAR = 2026;

export function AccountingPeriodSection() {
  const { toast, showToast } = useToast();
  const [periods, setPeriods] = useState<AccountingPeriod[]>([]);
  const [config, setConfig] = useState<PeriodConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await accountingPeriodsApi.list();
      setPeriods(r.periods); setConfig(r.config);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const yearPeriods = periods.filter((p) => p.year === YEAR && p.month != null);
  const now = new Date();
  const currentMonth = now.getFullYear() === YEAR ? now.getMonth() + 1 : 7;

  const monthState = (month: number): 'locked' | 'current' | 'future' => {
    const p = yearPeriods.find((pp) => pp.month === month);
    if (p?.status === 'LOCKED') return 'locked';
    if (month === currentMonth) return 'current';
    if (month < currentMonth) return 'locked';
    return 'future';
  };

  const lockedCount = yearPeriods.filter((p) => p.status === 'LOCKED').length;
  const history = yearPeriods
    .filter((p) => p.lockedAt || p.unlockedAt)
    .sort((a, b) => {
      const ta = new Date(a.unlockedAt || a.lockedAt || 0).getTime();
      const tb = new Date(b.unlockedAt || b.lockedAt || 0).getTime();
      return tb - ta;
    });

  const handleLockCurrent = async () => {
    if (!window.confirm(`Khoá kỳ T${currentMonth}/${YEAR}?\n\nSau khi khoá, không thể tạo/sửa/xoá chứng từ trong kỳ.`)) return;
    try {
      await accountingPeriodsApi.lock(YEAR, currentMonth);
      showToast(`Đã khoá kỳ T${currentMonth}/${YEAR}`);
      load();
    } catch (e) { alert(e instanceof Error ? e.message : String(e)); }
  };

  const handleUnlock = async (month: number) => {
    const reason = window.prompt(`Lý do mở khoá kỳ T${month}/${YEAR}? (bắt buộc)`);
    if (!reason?.trim()) return;
    try {
      await accountingPeriodsApi.unlock(YEAR, month, reason.trim());
      showToast(`Đã mở khoá kỳ T${month}/${YEAR}`);
      load();
    } catch (e) { alert(e instanceof Error ? e.message : String(e)); }
  };

  const saveConfig = async () => {
    if (!config) return;
    try {
      await accountingPeriodsApi.updateConfig(config);
      showToast('Đã lưu cấu hình kỳ kế toán');
    } catch (e) { alert(e instanceof Error ? e.message : String(e)); }
  };

  const handleCreateYear = async () => {
    const suggested = String(YEAR + 1);
    const input = window.prompt(
      `Tạo 12 kỳ tháng cho năm tài chính mới.\n\nHệ thống chỉ tạo các kỳ chưa có (skip kỳ đã tồn tại). Số dư đầu kỳ và kết chuyển làm ở tab "Số dư đầu kỳ".\n\nNhập năm mới:`,
      suggested,
    );
    if (!input) return;
    const y = Number(input);
    if (!Number.isInteger(y) || y < 2020 || y > 2100) {
      return alert('Năm không hợp lệ (2020-2100).');
    }
    try {
      const r = await accountingPeriodsApi.createYear(y);
      showToast(r.message);
      load();
    } catch (e) { alert(e instanceof Error ? e.message : String(e)); }
  };

  return (
    <>
      {err && <div className="pay-table-error">Lỗi: {err}</div>}

      <div className="per-hero">
        <div className="per-hero-icon"><svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="4" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg></div>
        <div className="per-hero-meta">
          <div className="per-hero-label">Năm tài chính hiện tại</div>
          <div className="per-hero-title">{YEAR} · 01/01/{YEAR} – 31/12/{YEAR}</div>
        </div>
        <div className="per-hero-actions">
          <button className="pay-btn-outline" type="button" onClick={handleCreateYear}>Chuyển năm tài chính</button>
          <button className="per-btn-danger" type="button" onClick={handleLockCurrent}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
            Khóa kỳ T{currentMonth}/{YEAR}
          </button>
        </div>
      </div>

      <div className="per-warn">
        <div className="per-warn-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><path d="M12 9v4M12 17h.01" /></svg></div>
        <div>
          <b>Sau khi khóa kỳ:</b> Không thể tạo / sửa / xóa chứng từ, ĐNC, phiếu chi trong kỳ đó. Chỉ <b>KTT</b> mới được mở lại kỳ đã khóa (kèm lý do lưu vào lịch sử).
        </div>
      </div>

      {loading ? <div className="pay-empty">Đang tải…</div> : (
        <>
          <div className="per-card">
            <div className="per-card-head">
              <div>
                <h3>Trạng thái các kỳ tháng năm {YEAR}</h3>
                <p className="sal-card-sub">
                  {lockedCount} kỳ đã khóa · Kỳ T{currentMonth} hiện tại · {12 - Math.max(currentMonth, lockedCount)} kỳ chưa tới
                </p>
              </div>
              <div className="per-legend">
                <span><i className="per-legend-dot locked" />Đã khóa</span>
                <span><i className="per-legend-dot current" />Đang mở</span>
                <span><i className="per-legend-dot future" />Chưa tới</span>
              </div>
            </div>
            <div className="per-months">
              {Array.from({ length: 12 }).map((_, i) => {
                const month = i + 1;
                const state = monthState(month);
                const p = yearPeriods.find((pp) => pp.month === month);
                return (
                  <div
                    key={month}
                    className={`per-month ${state}`}
                    onClick={() => p?.status === 'LOCKED' && handleUnlock(month)}
                    style={p?.status === 'LOCKED' ? { cursor: 'pointer' } : undefined}
                    title={p?.status === 'LOCKED' ? 'Click để mở khoá (yêu cầu lý do)' : ''}
                  >
                    <div className="per-month-label">T{month}</div>
                    <div className="per-month-state">{state === 'locked' ? 'Đã khóa' : state === 'current' ? 'Đang mở · Nay' : 'Chưa tới'}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="per-body-grid">
            <div className="per-card">
              <div className="per-card-head"><h3>Cấu hình kỳ kế toán</h3></div>
              {config && (
                <div className="per-cfg">
                  <div className="pfm-field">
                    <label>Kỳ báo cáo mặc định</label>
                    <AppSelect value={config.reportPeriodDefault} onChange={(e) => setConfig({ ...config, reportPeriodDefault: e.target.value })}>
                      <option value="thang">Tháng</option>
                      <option value="quy">Quý</option>
                      <option value="nam">Năm</option>
                    </AppSelect>
                  </div>
                  <div className="pfm-field">
                    <label>Ngày đầu năm tài chính</label>
                    <AppSelect value={config.fiscalYearStart} onChange={(e) => setConfig({ ...config, fiscalYearStart: e.target.value })}>
                      <option value="01-01">01/01 (mặc định)</option>
                      <option value="01-04">01/04</option>
                      <option value="01-07">01/07</option>
                      <option value="01-10">01/10</option>
                    </AppSelect>
                  </div>
                  <div className="pfm-field">
                    <label>Nguyên tắc ghi nhận</label>
                    <AppSelect value={config.accountingBasis} onChange={(e) => setConfig({ ...config, accountingBasis: e.target.value })}>
                      <option value="accrual">Dồn tích (Accrual)</option>
                      <option value="cash">Tiền mặt (Cash)</option>
                    </AppSelect>
                  </div>
                  <label className="per-toggle">
                    <input type="checkbox" checked={config.autoLockNextMonth} onChange={(e) => setConfig({ ...config, autoLockNextMonth: e.target.checked })} />
                    <span>Tự động khóa kỳ vào ngày {config.autoLockDay} tháng kế tiếp</span>
                  </label>
                  <button className="pay-btn-primary" type="button" onClick={saveConfig}>Lưu cấu hình</button>
                </div>
              )}
            </div>

            <div className="per-card">
              <div className="per-card-head">
                <div>
                  <h3>Lịch sử khóa kỳ</h3>
                  <p className="sal-card-sub">{history.length} thao tác trong năm</p>
                </div>
              </div>
              <table className="per-tbl">
                <thead>
                  <tr>
                    <th>Kỳ</th>
                    <th>Thao tác</th>
                    <th>Người thực hiện</th>
                    <th>Thời điểm</th>
                  </tr>
                </thead>
                <tbody>
                  {history.length === 0 && (
                    <tr><td colSpan={4} className="pay-empty">Chưa có thao tác khoá/mở kỳ nào.</td></tr>
                  )}
                  {history.map((p) => (
                    <>
                      {p.unlockedAt && (
                        <tr key={`${p.id}-u`}>
                          <td className="sal-mono">T{p.month}/{p.year}</td>
                          <td><span className="per-action-pill unlock">Mở lại</span></td>
                          <td>{p.unlockedBy}</td>
                          <td>
                            {new Date(p.unlockedAt).toLocaleString('vi-VN')}
                            {p.unlockReason && <div className="per-reason">Lý do: {p.unlockReason}</div>}
                          </td>
                        </tr>
                      )}
                      {p.lockedAt && (
                        <tr key={`${p.id}-l`}>
                          <td className="sal-mono">T{p.month}/{p.year}</td>
                          <td><span className="per-action-pill lock">Khóa kỳ</span></td>
                          <td>{p.lockedBy}</td>
                          <td>{new Date(p.lockedAt).toLocaleString('vi-VN')}</td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {toast && <div className="coa-toast">{toast}</div>}
    </>
  );
}
